/**
 * cortex-admin の claude-usage API contract（zod schema + 型）。
 * API（apps/api/admin）と Web（apps/web/cortex-admin）が共有する。
 *
 * 母艦の bot が Claude Code の OTel テレメトリを Grafana Cloud へ送っており、それを読んで
 * 「どの bot が何トークン使い、どのツールを何回叩いたか」を出す。
 *
 * **トークンとツールで取得元が違う。**
 *   - トークン / コスト … メトリクス（Prometheus / Mimir）
 *   - ツール使用       … ログ（Loki）の `claude_code.tool_result` イベント
 * ツール名はメトリクスに乗らないため、ログ側を引かないと 1 件も取れない。
 *
 * **MCP ツールは個別名が取れない。** Claude Code は MCP 由来のツールを一律 `mcp_tool` として
 * 送り、サーバー名もツール名も落とす（実測）。「MCP をどれだけ使ったか」までが上限で、
 * 「どの MCP か」は BQ の claude_events（セッション履歴由来）側にしか無い。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business bot 別の Claude 利用状況（トークン / コスト / ツール使用）の API ↔ Web 型を共有する。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 集計期間。Grafana のクエリ窓にそのまま渡す。
 *
 * メトリクスは単調増加カウンタなので `increase()` で窓内の増分を取る。プロセス再起動で
 * カウンタが 0 に戻るが `increase()` がリセットを補正する（生値の差分だと再起動のたびに
 * 負の増分になり、実際より少なく出る）。
 *
 * @graph-connects none
 */
export const ClaudeUsageRangeSchema = z.enum(['1h', '24h', '7d']);
/**
 * bot 1 つ分のトークン消費。
 *
 * **`cacheRead` が支配的で、`input` / `output` だけ見ると実態を見誤る。** 実測では
 * cacheRead が input の 3 万倍近くになる場面があった。4 種を分けて持つのはこのため。
 *
 * @graph-connects none
 */
export const ClaudeTokenUsageSchema = z.object({
    input: z.number().nonnegative(),
    output: z.number().nonnegative(),
    cacheRead: z.number().nonnegative(),
    cacheCreation: z.number().nonnegative(),
});
/**
 * ツール 1 種の使用回数。
 *
 * `name` が `mcp_tool` の行は **MCP ツール全体の合計**であって特定のツールではない
 * （Claude Code が個別名を送らない）。画面はここを「MCP（個別名なし）」と明示すること。
 *
 * @graph-connects none
 */
export const ClaudeToolUsageSchema = z.object({
    name: z.string().min(1),
    count: z.number().nonnegative(),
    /** 失敗した呼び出し数。`success="false"` の件数。 */
    failures: z.number().nonnegative(),
});
/**
 * bot 1 つ分の利用状況。
 *
 * `mode` は cortex ユーザーが 1 人で 3 セッション（reviewer / annotation-reviewer / inspector）を
 * 動かすため必要。ユーザー名だけでは役割を分けられない。
 *
 * @graph-connects none
 */
export const ClaudeBotUsageSchema = z.object({
    /** OS ユーザー名。母艦の bot 分離の単位で、bot-status ページの表記と一致する。 */
    bot: z.string().min(1),
    /** 起動モード（reviewer / author / annotation-reviewer / inspector 等）。取れない場合は undefined。 */
    mode: z.string().optional(),
    tokens: ClaudeTokenUsageSchema,
    /** 期間内のコスト（USD）。Claude Code が算出した値をそのまま使う。 */
    costUsd: z.number().nonnegative(),
    /** 期間内のセッション数。 */
    sessions: z.number().nonnegative(),
    /** 使用回数の多い順のツール一覧。 */
    tools: z.array(ClaudeToolUsageSchema),
});
/** 取得リクエスト。 @graph-connects none */
export const ClaudeUsageLatestInputSchema = z.object({
    range: ClaudeUsageRangeSchema.default('24h'),
});
/**
 * 取得レスポンス。
 *
 * **`unavailable` を持つのは、取得失敗を「利用ゼロ」と見せないため。** Grafana が落ちていても
 * 空配列を返すと画面は「今日は誰も使っていない」と読めてしまう。bot-status で
 * backupAgeHours を null にしたのと同じ理由。
 *
 * @graph-connects none
 */
export const ClaudeUsageLatestResponseSchema = z.object({
    range: ClaudeUsageRangeSchema,
    /** 集計時刻（ISO8601）。 */
    fetchedAt: z.iso.datetime(),
    bots: z.array(ClaudeBotUsageSchema),
    /** 取得できなかった場合の理由。設定されていれば bots は信用できない。 */
    unavailable: z.string().optional(),
});
//# sourceMappingURL=claude-usage.js.map